import { Option } from '@/constants/general';
import { autoUpdate, flip, offset, size, useFloating } from '@floating-ui/react';
import { Combobox, Transition } from '@headlessui/react';
import { useClickOutside } from '@mantine/hooks';
import { useVirtualizer } from '@tanstack/react-virtual';
import cn from 'classnames';
import { Fragment, forwardRef, useMemo, useRef, useState } from 'react';
import { BsXCircleFill } from 'react-icons/bs';
import { MdUnfoldMore } from 'react-icons/md';
import PortalCurrencyIcon from '../common/PortalCurrencyIcon';

interface MultipleSelectProps {
    disableSorting?: boolean;
    options: Option[];
    selected?: Option[];
    error?: string;
    placeholder?: string;
    enableCurrencyIcon?: boolean;
    onChange?: (e: Option[]) => void;
    disabled?: boolean;
}

const dropDownClassName = 'w-full bg-brand-background-dark rounded-md py-1 text-sm border border-brand-neutral-700';

const MultipleSelect = forwardRef((props: MultipleSelectProps, _ref) => {
    const {
        options,
        selected = [],
        placeholder,
        onChange,
        enableCurrencyIcon = false,
        disableSorting = false,
        disabled = false
    } = props;

    const [query, setQuery] = useState<any>('');
    const [inputFocused, setInputFocused] = useState<boolean>(false);
    const clickOutsideRef = useClickOutside(() => setInputFocused(false));

    const middleware = useMemo(() => {
        return [
            size({
                apply({ rects, elements }) {
                    Object.assign(elements.floating.style, {
                        width: `${rects.reference.width}px`
                    });
                }
            }),
            offset(8),
            flip()
        ];
    }, []);

    const { refs, floatingStyles } = useFloating({
        strategy: 'fixed',
        placement: 'bottom-start',
        whileElementsMounted: autoUpdate,
        middleware
    });

    const hasSelected = useMemo(() => selected.length > 0, [selected]);

    const sortedOptions = useMemo(() => {
        return disableSorting ? options : options && options.sort((a, b) => a.label?.localeCompare(b.label));
    }, [options, disableSorting]);

    const filteredOption = useMemo(() => {
        const unselected = sortedOptions
            ? sortedOptions.filter((option) => !selected.find((s) => s.value === option.value))
            : [];
        return query === ''
            ? unselected
            : unselected.filter((option) =>
                  option.label.toLowerCase().replace(/\s+/g, '').includes(query.toLowerCase().replace(/\s+/g, ''))
              );
    }, [selected, sortedOptions, query]);

    return (
        <Combobox ref={clickOutsideRef} value={selected} onChange={onChange} multiple disabled={disabled} as="div">
            <div ref={refs.setReference} className="relative min-w-[150px] w-full">
                <div className="relative w-full overflow-hidden rounded-md bg-brand-background-dark border border-brand-neutral-700 text-left text-sm">
                    <Combobox.Button
                        className={cn('flex flex-col w-full', {
                            'cursor-not-allowed': disabled
                        })}
                        onClick={() => setInputFocused(true)}>
                        {(hasSelected || !inputFocused) && (
                            <div
                                className={cn('flex flex-wrap gap-x-3 gap-y-1 py-2 pl-3 pr-10 text-brand-neutral-500', {
                                    'pb-0': hasSelected && inputFocused
                                })}>
                                <SelectedOptions
                                    placeholder={placeholder}
                                    hasSelected={hasSelected}
                                    selected={selected}
                                    onChange={onChange}
                                    disabled={disabled}
                                    enableCurrencyIcon={enableCurrencyIcon}
                                />
                            </div>
                        )}
                        {inputFocused && (
                            <Combobox.Input
                                className="w-full border-none py-2 pl-3 pr-10 text-sm leading-5 text-brand-neutral-200 placeholder:text-brand-neutral-500 bg-brand-background-dark outline-none"
                                placeholder={placeholder}
                                onChange={(event) => setQuery(event.target.value)}
                                onBlur={(e) => e.preventDefault()}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        if (filteredOption && filteredOption.length > 0) {
                                            onChange?.([...selected, filteredOption[0]]);
                                            e.preventDefault();
                                        }
                                    }
                                }}
                            />
                        )}
                        <div className="absolute inset-y-0 right-0 flex items-center pr-2">
                            <MdUnfoldMore className="h-5 w-5 text-brand-neutral-200" aria-hidden="true" />
                        </div>
                    </Combobox.Button>
                </div>
                <Transition
                    as={Fragment}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                    afterLeave={() => setQuery('')}>
                    <Combobox.Options ref={refs.setFloating} style={floatingStyles} className="z-50">
                        {filteredOption && filteredOption.length === 0 && query !== '' ? (
                            <div className={dropDownClassName}>
                                <div className="relative cursor-default select-none py-2 px-4 text-brand-neutral-200">
                                    Nothing found.
                                </div>
                            </div>
                        ) : (
                            <>
                                {filteredOption && filteredOption.length > 0 ? (
                                    <VirtualizedList
                                        items={filteredOption}
                                        selected={selected}
                                        onChange={(e) => {
                                            onChange?.(e);
                                        }}
                                        dropDownClassName={dropDownClassName}
                                        enableCurrencyIcon={enableCurrencyIcon}
                                    />
                                ) : (
                                    <div className={dropDownClassName}>
                                        <div className="relative cursor-default select-none py-2 px-4 text-brand-neutral-200">
                                            No Available Options
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </Combobox.Options>
                </Transition>
            </div>
            {props.error ? (
                <div className="pt-2 text-xs text-secondary sm:text-sm text-right">{props.error}</div>
            ) : null}
        </Combobox>
    );
});

MultipleSelect.displayName = 'MultipleSelect';

export default MultipleSelect;

const SelectedOptions = ({ placeholder, hasSelected, selected, onChange, disabled, enableCurrencyIcon }: any) => {
    if (hasSelected) {
        return (
            <Fragment>
                {selected.map((option) => (
                    <div
                        key={option.value}
                        className={cn('flex items-center gap-2 text-brand-neutral-200 text-sm', {})}>
                        <div className="flex items-center gap-1">
                            {enableCurrencyIcon && <PortalCurrencyIcon show={option.label} className="!w-3 !h-3" />}
                            <div className="w-fit break-all line-clamp-1">{option?.label || ''}</div>
                        </div>
                        <button
                            type="button"
                            onClick={(e) => {
                                onChange(selected.filter((s) => s.value !== option.value));
                                e.preventDefault();
                            }}
                            className={cn('w-3 h-3 cursor-pointer', {
                                'hover:text-brand-secondary-light': !disabled,
                                'cursor-not-allowed': disabled
                            })}>
                            <BsXCircleFill className={cn('w-full h-full')} />
                        </button>
                    </div>
                ))}
            </Fragment>
        );
    } else {
        return <span>{placeholder}</span>;
    }
};

interface VirtualizedListProps {
    items: Option[];
    selected: Option[];
    onChange: (e: Option[]) => void;
    dropDownClassName: string;
    enableCurrencyIcon?: boolean;
}

function VirtualizedList({
    items,
    selected,
    onChange,
    dropDownClassName,
    enableCurrencyIcon = false
}: VirtualizedListProps) {
    const parentRef = useRef<HTMLDivElement>(null);

    const virtualizer = useVirtualizer({
        count: items?.length,
        getScrollElement: () => parentRef.current,
        estimateSize: () => 35,
        overscan: 5
    });

    const dropDownHeight = useMemo(
        () => (items?.length > 5 ? 200 : virtualizer.getTotalSize() + 10),
        [items?.length, virtualizer]
    );

    const virtualizedItems = virtualizer.getVirtualItems();

    return (
        <div
            ref={parentRef}
            style={{
                height: dropDownHeight,
                overflowY: 'auto',
                contain: 'strict'
            }}
            className={dropDownClassName}>
            <div
                style={{
                    height: virtualizer.getTotalSize(),
                    width: '100%',
                    position: 'relative'
                }}>
                {virtualizedItems.map((virtualRow: any) => (
                    <button
                        key={virtualRow.index}
                        type="button"
                        data-index={virtualRow.index}
                        ref={virtualizer.measureElement}
                        className="relative flex items-center gap-1 text-left cursor-pointer hover:bg-brand-primary select-none py-2 pl-3 pr-4"
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            transform: `translateY(${virtualRow.start}px)`
                        }}
                        onClick={() => {
                            onChange([...selected, items?.[virtualRow.index]]);
                        }}>
                        {enableCurrencyIcon && <PortalCurrencyIcon show={items?.[virtualRow.index]?.label} />}
                        <span className={cn(`block truncate whitespace-normal`)}>
                            {items?.[virtualRow.index]?.label}
                        </span>
                    </button>
                ))}
            </div>
        </div>
    );
}
